<template>
  <div class="html-editor flex flex-col h-full bg-white">
    <!-- 工具栏 -->
    <div class="toolbar border-b border-gray-200 bg-gray-50 px-4 py-2 flex items-center justify-between">
      <!-- 左侧：视图模式切换 -->
      <div class="flex items-center space-x-2">
        <div class="inline-flex rounded-md shadow-sm" role="group">
          <button
            @click="viewMode = 'preview'"
            :class="[
              'px-3 py-1.5 text-xs font-medium border',
              viewMode === 'preview' 
                ? 'bg-blue-600 text-white border-blue-600 z-10' 
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            ]"
            class="rounded-l-md"
          >
            <EyeIcon class="w-4 h-4 inline mr-1" />
            可视
          </button>
          <button
            @click="viewMode = 'split'"
            :class="[
              'px-3 py-1.5 text-xs font-medium border-t border-b',
              viewMode === 'split' 
                ? 'bg-blue-600 text-white border-blue-600 z-10' 
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            ]"
          >
            <Bars3BottomLeftIcon class="w-4 h-4 inline mr-1" />
            分屏
          </button>
          <button
            @click="viewMode = 'code'"
            :class="[
              'px-3 py-1.5 text-xs font-medium border',
              viewMode === 'code' 
                ? 'bg-blue-600 text-white border-blue-600 z-10' 
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            ]"
            class="rounded-r-md"
          >
            <CodeBracketIcon class="w-4 h-4 inline mr-1" />
            代码
          </button>
        </div>
        
        <div class="h-6 w-px bg-gray-300 mx-2"></div>
        
        <!-- 设备预览选择 -->
        <div class="inline-flex rounded-md shadow-sm" role="group">
          <button
            @click="previewDevice = 'desktop'"
            :class="[
              'px-3 py-1.5 text-xs font-medium border',
              previewDevice === 'desktop' 
                ? 'bg-blue-600 text-white border-blue-600 z-10' 
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            ]"
            class="rounded-l-md"
            title="桌面预览"
          >
            <ComputerDesktopIcon class="w-4 h-4 inline" />
          </button>
          <button
            @click="previewDevice = 'tablet'"
            :class="[
              'px-3 py-1.5 text-xs font-medium border-t border-b',
              previewDevice === 'tablet' 
                ? 'bg-blue-600 text-white border-blue-600 z-10' 
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            ]"
            title="平板预览"
          >
            <DeviceTabletIcon class="w-4 h-4 inline" />
          </button>
          <button
            @click="previewDevice = 'mobile'"
            :class="[
              'px-3 py-1.5 text-xs font-medium border',
              previewDevice === 'mobile' 
                ? 'bg-blue-600 text-white border-blue-600 z-10' 
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            ]"
            class="rounded-r-md"
            title="手机预览"
          >
            <DevicePhoneMobileIcon class="w-4 h-4 inline" />
          </button>
        </div>
        
        <div class="h-6 w-px bg-gray-300 mx-2"></div>
        
        <!-- 快捷编辑工具 -->
        <button
          @click="showTextReplacer = !showTextReplacer"
          class="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          title="批量替换文字"
        >
          <MagnifyingGlassIcon class="w-4 h-4 inline mr-1" />
          替换
        </button>
        
      </div>
      
      <!-- 右侧：操作按钮 -->
      <div class="flex items-center space-x-2">
        <button
          @click="refreshPreview"
          :disabled="isRefreshing"
          class="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
          title="刷新预览"
        >
          <ArrowPathIcon class="w-4 h-4 inline mr-1" :class="{ 'animate-spin': isRefreshing }" />
          刷新
        </button>
      </div>
    </div>
    
    <!-- 文字替换弹窗 -->
    <div v-if="showTextReplacer" class="absolute top-14 left-32 z-20 bg-white border border-gray-300 rounded-lg shadow-xl p-4 w-80">
      <div class="flex items-center justify-between mb-3">
        <h4 class="text-sm font-medium text-gray-900">批量替换文字</h4>
        <button @click="showTextReplacer = false" class="text-gray-400 hover:text-gray-600">
          <XMarkIcon class="w-4 h-4" />
        </button>
      </div>
      <div class="space-y-3">
        <div>
          <label class="block text-xs text-gray-700 mb-1">查找：</label>
          <input v-model="searchText" type="text" placeholder="输入要查找的文字" 
                 class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label class="block text-xs text-gray-700 mb-1">替换为：</label>
          <input v-model="replaceText" type="text" placeholder="输入替换后的文字" 
                 class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" />
        </div>
        <button
          @click="replaceAllText"
          :disabled="!searchText"
          class="w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          全部替换
        </button>
      </div>
    </div>
    
    <!-- 链接编辑模态框 -->
    <div v-if="showLinkDialog" class="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50" @click.self="closeLinkDialog">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4" @click.stop>
        <div class="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
          <h3 class="text-base font-semibold text-gray-800">{{ isEditingLink ? '编辑链接' : '添加链接' }}</h3>
          <button @click="closeLinkDialog" class="text-gray-400 hover:text-gray-600 transition-colors">
            <XMarkIcon class="w-5 h-5" />
          </button>
        </div>
        
        <div class="px-5 py-4 space-y-3">
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1.5">链接地址</label>
            <input
              v-model="linkUrl"
              type="text"
              placeholder="https://example.com"
              class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
              @keyup.enter="applyLink"
            />
          </div>
          
          <!-- 只在链接包含文字时显示"链接文字"字段 -->
          <div v-if="!linkContainsOnlySvg">
            <label class="block text-xs font-medium text-gray-700 mb-1.5">链接文字</label>
            <input
              v-model="linkText"
              type="text"
              placeholder="点击这里"
              class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
              @keyup.enter="applyLink"
            />
          </div>
          
          <!-- 只在链接包含文字时显示"字体颜色"字段 -->
          <div v-if="!linkContainsOnlySvg">
            <label class="block text-xs font-medium text-gray-700 mb-1.5">字体颜色</label>
            <div class="flex items-center gap-2">
              <input
                v-model="linkColor"
                type="color"
                class="w-12 h-10 border border-gray-200 rounded-lg cursor-pointer"
                title="选择字体颜色"
              />
              <input
                v-model="linkColor"
                type="text"
                placeholder="#3B82F6"
                class="flex-1 px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none font-mono"
              />
            </div>
          </div>
          
          <label class="flex items-center space-x-2 cursor-pointer py-1">
            <input
              v-model="linkTargetBlank"
              type="checkbox"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="text-sm text-gray-700">在新窗口打开</span>
          </label>
        </div>
        
        <div class="px-5 py-3.5 bg-gray-50 border-t border-gray-100 rounded-b-xl flex justify-between">
          <button
            v-if="isEditingLink"
            @click="removeLink"
            class="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            移除链接
          </button>
          <div class="flex gap-2" :class="{ 'ml-auto': !isEditingLink }">
            <button
              @click="closeLinkDialog"
              class="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              取消
            </button>
            <button
              @click="applyLink"
              :disabled="!linkUrl"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50"
            >
              {{ isEditingLink ? '更新' : '确定' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 视频编辑模态框 -->
    <div v-if="showVideoDialog" class="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50" @click.self="closeVideoDialog">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4" @click.stop>
        <div class="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
          <h3 class="text-base font-semibold text-gray-800 flex items-center gap-2">
            <span class="text-xl">🎬</span>
            编辑YouTube视频
          </h3>
          <button @click="closeVideoDialog" class="text-gray-400 hover:text-gray-600 transition-colors">
            <XMarkIcon class="w-5 h-5" />
          </button>
        </div>
        
        <div class="px-5 py-4 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">YouTube视频ID或URL</label>
            <input
              v-model="videoId"
              type="text"
              placeholder="dQw4w9WgXcQ 或 https://youtube.com/watch?v=..."
              class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
              @keyup.enter="saveVideoId"
            />
            <p class="text-xs text-gray-500 mt-1.5">
              输入11位视频ID（如 dQw4w9WgXcQ）或完整的YouTube链接
            </p>
          </div>
          
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <h5 class="text-xs font-semibold text-blue-900 mb-2 flex items-center gap-1">
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
              </svg>
              如何获取视频ID？
            </h5>
            <ul class="text-xs text-blue-800 space-y-1">
              <li>• 从 <code class="bg-blue-100 px-1 rounded">youtube.com/watch?v=<strong>dQw4w9WgXcQ</strong></code> 提取 ID</li>
              <li>• 从 <code class="bg-blue-100 px-1 rounded">youtu.be/<strong>dQw4w9WgXcQ</strong></code> 提取 ID</li>
              <li>• 或直接粘贴完整URL，系统会自动提取ID</li>
            </ul>
          </div>
        </div>
        
        <div class="px-5 py-3.5 bg-gray-50 border-t border-gray-100 rounded-b-xl flex justify-end gap-2">
          <button
            @click="closeVideoDialog"
            class="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            取消
          </button>
          <button
            @click="saveVideoId"
            :disabled="!videoId"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2"
          >
            更新视频
          </button>
        </div>
      </div>
    </div>
    
    <!-- 可视化编辑模态框 -->
    <div v-if="showEditModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" @click.self="closeEditModal">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4" @click.stop>
        <div class="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
          <div class="flex items-center space-x-2">
            <h3 class="text-base font-semibold text-gray-800">编辑内容</h3>
            <span class="px-2 py-0.5 text-xs font-mono bg-blue-50 text-blue-600 rounded">&lt;{{ editingTagName }}&gt;</span>
          </div>
          <button @click="closeEditModal" class="text-gray-400 hover:text-gray-600 transition-colors">
            <XMarkIcon class="w-5 h-5" />
          </button>
        </div>
        
        <div class="px-5 py-4">
          <!-- 文本编辑工具栏（仅在非图片编辑时显示） -->
          <div v-if="!isEditingImage" class="mb-4 flex items-center gap-1 p-2 bg-white border border-gray-200 rounded-lg shadow-sm">
            <!-- 文字样式 -->
            <button
              @click="applyFormat('bold')"
              class="w-8 h-8 flex items-center justify-center text-sm font-bold text-gray-700 hover:bg-gray-100 rounded transition-colors"
              title="加粗"
            >
              B
            </button>
            <button
              @click="applyFormat('italic')"
              class="w-8 h-8 flex items-center justify-center text-sm italic text-gray-700 hover:bg-gray-100 rounded transition-colors"
              title="斜体"
            >
              I
            </button>
            <button
              @click="applyFormat('underline')"
              class="w-8 h-8 flex items-center justify-center text-sm underline text-gray-700 hover:bg-gray-100 rounded transition-colors"
              title="下划线"
            >
              U
            </button>
            
            <div class="w-px h-6 bg-gray-200 mx-1"></div>
            
            <!-- 文字颜色 -->
            <div class="flex items-center gap-1">
              <input
                v-model="textColor"
                type="color"
                @change="applyTextColor"
                class="w-8 h-8 border border-gray-200 rounded cursor-pointer"
                title="文字颜色"
              />
            </div>
            
            <!-- 背景颜色 -->
            <div class="flex items-center gap-1">
              <input
                v-model="bgColor"
                type="color"
                @change="applyBgColor"
                class="w-8 h-8 border border-gray-200 rounded cursor-pointer"
                title="背景颜色"
              />
            </div>
            
            <div class="w-px h-6 bg-gray-200 mx-1"></div>
            
            <!-- 字体大小 -->
            <select
              v-model="fontSize"
              @change="applyFontSize"
              class="text-xs border border-gray-200 rounded px-3 h-8 min-w-[80px] bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer appearance-none bg-no-repeat bg-right pr-7"
              style="background-image: url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 20 20%27 fill=%27none%27%3e%3cpath d=%27M7 7l3 3 3-3%27 stroke=%27%239CA3AF%27 stroke-width=%271.5%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27/%3e%3c/svg%3e'); background-position: right 0.5rem center; background-size: 1.2em 1.2em;"
              title="字体大小"
            >
              <option value="">默认</option>
              <option value="12px">12px</option>
              <option value="14px">14px</option>
              <option value="16px">16px</option>
              <option value="18px">18px</option>
              <option value="20px">20px</option>
              <option value="24px">24px</option>
              <option value="28px">28px</option>
              <option value="32px">32px</option>
              <option value="36px">36px</option>
              <option value="48px">48px</option>
            </select>
            
            <div class="w-px h-6 bg-gray-200 mx-1"></div>
            
            <!-- 链接 -->
            <button
              @click="openLinkDialog"
              class="w-8 h-8 flex items-center justify-center text-base hover:bg-blue-50 rounded transition-colors"
              title="链接"
            >
              🔗
            </button>
            
            <!-- 清除格式 -->
            <button
              @click="clearFormat"
              class="px-2 h-8 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded transition-colors"
              title="清除格式"
            >
              ✕
            </button>
          </div>
          
          
          <!-- 图片编辑区 -->
          <div v-if="isEditingImage" class="space-y-3">
            <div>
              <input
                ref="imageFileInput"
                type="file"
                accept="image/*"
                @change="handleImageUpload"
                class="hidden"
              />
              <button
                @click="$refs.imageFileInput.click()"
                class="w-full px-4 py-2.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
              >
                <span class="text-lg">📤</span>
                <span>选择图片上传</span>
              </button>
            </div>
            
            <div class="relative">
              <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-gray-200"></div>
              </div>
              <div class="relative flex justify-center text-xs">
                <span class="px-2 bg-white text-gray-400">或</span>
              </div>
            </div>
            
            <div>
              <input
                v-model="imageSrc"
                type="text"
                placeholder="输入图片链接 (https://...)"
                class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
              />
            </div>
            
            <div>
              <input
                v-model="imageAlt"
                type="text"
                placeholder="图片描述 (可选)"
                class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
              />
            </div>
            
            <!-- 图片预览 -->
            <div v-if="imageSrc" class="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <img :src="imageSrc" :alt="imageAlt" class="w-full max-h-48 object-contain rounded" @error="toast.error('图片加载失败')" />
            </div>
          </div>
          
          <!-- 普通内容编辑区 -->
          <div v-else>
            <div
              ref="contentEditor"
              contenteditable="true"
              @input="onContentInput"
              @blur="onContentBlur"
              class="w-full min-h-[160px] px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none text-sm overflow-y-auto max-h-[320px] bg-white"
              style="white-space: pre-wrap;"
              placeholder="输入内容..."
            ></div>
            <p class="text-xs text-gray-400 mt-2">选中文字后使用工具栏设置样式</p>
          </div>
        </div>
        
        <div class="px-5 py-3.5 bg-gray-50 border-t border-gray-100 rounded-b-xl flex justify-end gap-2">
          <button
            @click="closeEditModal"
            class="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            取消
          </button>
          <button
            @click="saveEdit"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            保存
          </button>
        </div>
      </div>
    </div>
    
    <!-- 主编辑区域 -->
    <div class="flex-1 flex overflow-hidden relative">
      <!-- 代码编辑器 -->
      <div 
        v-show="viewMode === 'code' || viewMode === 'split'" 
        :class="viewMode === 'split' ? 'w-1/2 border-r border-gray-200' : 'w-full'"
        class="flex flex-col"
      >
        <div class="px-3 py-2 bg-gray-800 text-white text-xs font-medium flex items-center justify-between">
          <span>HTML 代码</span>
          <span class="text-gray-400">{{ htmlContent.length }} 字符</span>
        </div>
        <textarea
          v-model="htmlContent"
          @input="onCodeChange"
          class="flex-1 w-full bg-gray-900 text-gray-100 font-mono text-xs p-4 resize-none border-0 focus:outline-none focus:ring-0"
          placeholder="<!DOCTYPE html>&#10;<html lang=&quot;en&quot;>&#10;<head>&#10;  <meta charset=&quot;UTF-8&quot;>&#10;  <title>{{ site_title }}</title>&#10;</head>&#10;<body>&#10;  ...&#10;</body>&#10;</html>"
          spellcheck="false"
        ></textarea>
      </div>
      
      <!-- 预览区域 -->
      <div 
        v-show="viewMode === 'preview' || viewMode === 'split'" 
        :class="viewMode === 'split' ? 'w-1/2' : 'w-full'"
        class="flex flex-col bg-gray-100"
      >
        <div class="flex-1 overflow-auto p-4 flex items-start justify-center">
          <div :class="deviceClasses" class="bg-white shadow-lg transition-all duration-300">
            <iframe
              ref="previewFrame"
              :srcdoc="previewContent"
              class="w-full h-full border-0"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, defineProps, defineEmits, defineExpose } from 'vue'
import { useToast } from 'vue-toastification'
import {
  CodeBracketIcon, EyeIcon, Bars3BottomLeftIcon,
  MagnifyingGlassIcon, ArrowPathIcon, XMarkIcon,
  ComputerDesktopIcon, DeviceTabletIcon, DevicePhoneMobileIcon
} from '@heroicons/vue/24/outline'

const toast = useToast()

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  height: {
    type: [String, Number],
    default: 600
  },
  previewSrc: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

// 状态
const htmlContent = ref(props.modelValue)
const viewMode = ref('preview') // 'code' | 'split' | 'preview' - 默认预览模式
const previewDevice = ref('desktop') // 'desktop' | 'tablet' | 'mobile'
const previewContent = ref('')
const isRefreshing = ref(false)
const previewFrame = ref(null)

// 工具状态
const showTextReplacer = ref(false)
const searchText = ref('')
const replaceText = ref('')
const skipNextPreviewUpdate = ref(false) // 跳过下一次预览更新（用于内部DOM操作）

// 计算设备预览尺寸
const deviceClasses = computed(() => {
  switch (previewDevice.value) {
    case 'mobile':
      return 'w-[375px] h-[667px] rounded-lg overflow-hidden'
    case 'tablet':
      return 'w-[768px] h-[1024px] rounded-lg overflow-hidden'
    default:
      return 'w-full h-full'
  }
})

// 监听 modelValue 变化
watch(() => props.previewSrc, () => {
  updatePreview()
})

watch(() => props.modelValue, (newValue) => {
  // 如果是内部DOM操作引起的更新，跳过预览刷新
  if (skipNextPreviewUpdate.value) {
    skipNextPreviewUpdate.value = false
    return
  }
  
  if (htmlContent.value !== newValue) {
    htmlContent.value = newValue
    updatePreview()
  }
})

// 监听内容变化，直接输出（不清理，让外部决定何时清理）
watch(htmlContent, (newValue) => {
  emit('update:modelValue', newValue)
  emit('change', newValue)
}, { immediate: true })

// 代码变化时更新预览（带防抖）
let updateTimer = null
const onCodeChange = () => {
  if (updateTimer) {
    clearTimeout(updateTimer)
  }
  updateTimer = setTimeout(() => {
    updatePreview()
  }, 500) // 500ms 防抖
}

// 更新预览
let previewToken = 0
const updatePreview = () => {
  if (props.previewSrc) {
    const token = ++previewToken
    fetch(props.previewSrc, { credentials: 'omit' })
      .then((res) => res.text())
      .then((text) => {
        if (token !== previewToken || !text) return
        previewContent.value = text
        nextTick(() => {
          setTimeout(() => waitForDOMReady(), 300)
        })
      })
      .catch(() => {
        if (token !== previewToken) return
        previewContent.value = htmlContent.value
        nextTick(() => {
          setTimeout(() => waitForDOMReady(), 300)
        })
      })
    return
  }
  // 清理当前iframe中的badge和编辑状态（如果存在）
  const iframe = previewFrame.value
  if (iframe && iframe.contentDocument) {
    const badges = iframe.contentDocument.querySelectorAll('.visual-edit-badge')
    badges.forEach(badge => badge.remove())
    
    // 清理section移动按钮
    const moveButtons = iframe.contentDocument.querySelectorAll('.section-move-buttons')
    moveButtons.forEach(btn => btn.remove())
    
    // 清理Hero图片编辑按钮
    const heroImageButtons = iframe.contentDocument.querySelectorAll('.hero-image-edit-button')
    heroImageButtons.forEach(btn => btn.remove())
    
    // 清理所有编辑状态的class
    iframe.contentDocument.querySelectorAll('.visual-edit-active, .visual-edit-hover, .section-movable').forEach(el => {
      el.classList.remove('visual-edit-active', 'visual-edit-hover', 'section-movable')
    })
  }
  
  previewContent.value = htmlContent.value
  
  // 使用轮询方式等待 DOM 就绪（不等待资源加载）
  // 添加一个延迟，让 iframe 有充足时间初始化和渲染
  nextTick(() => {
    setTimeout(() => {
      waitForDOMReady()
    }, 300) // 延迟300ms再开始检查，确保DOM稳定
  })
}

// 轮询等待 iframe DOM 就绪（不等待资源加载）
const waitForDOMReady = () => {
  const iframe = previewFrame.value
  if (!iframe) return
  
  let attempts = 0
  const checkInterval = 150 // 每次检查间隔150ms（更稳定）
  const maxAttempts = 40 // 最多尝试 6 秒（40 * 150ms）
  let lastBodyChildrenCount = 0
  let stableCount = 0 // 内容稳定计数
  
  const checkReady = () => {
    attempts++
    
    try {
      const doc = iframe.contentDocument
      const readyState = doc?.readyState
      const hasBody = !!doc?.body
      const bodyChildrenCount = doc?.body?.children?.length || 0
      
      // 检测内容是否稳定（子元素数量不再变化）
      if (bodyChildrenCount === lastBodyChildrenCount && bodyChildrenCount > 0) {
        stableCount++
      } else {
        stableCount = 0 // 重置稳定计数
      }
      lastBodyChildrenCount = bodyChildrenCount
      
      // 主要注入条件：DOM就绪 + 内容充足 + 连续2次检查内容稳定
      if (doc && hasBody && 
          readyState !== 'loading' && 
          bodyChildrenCount >= 5 && 
          stableCount >= 2) {
        onPreviewLoad()
        return
      }
      
      // 快速通道：如果子元素很多且已complete，提前注入
      if (doc && hasBody && 
          readyState === 'complete' && 
          bodyChildrenCount >= 10 &&
          attempts >= 3) {
        onPreviewLoad()
        return
      }
      
      // 容错机制：等待1.5秒后，如果有任何内容就注入
      if (attempts >= 10 && doc && hasBody && bodyChildrenCount > 0) {
        onPreviewLoad()
        return
      }
      
    } catch (error) {
      // 跨域或其他错误，静默处理
    }
    
    // 如果还没就绪且未超时，继续检查
    if (attempts < maxAttempts) {
      setTimeout(checkReady, checkInterval)
    } else {
      // 超时后强制注入（最后的兜底）
      try {
        onPreviewLoad()
      } catch (error) {
        // 静默处理
      }
    }
  }
  
  // 立即开始第一次检查
  checkReady()
}

// 刷新预览
const refreshPreview = () => {
  isRefreshing.value = true
  updatePreview()
  setTimeout(() => {
    isRefreshing.value = false
  }, 500)
}

// 可编辑元素选择器（包含div和img）
const editableSelectors = 'h1, h2, h3, h4, h5, h6, p, a, button, span, li, td, th, label, img, div'

// 可移动的section选择器
const movableSelectors = 'section'

// 预览加载完成 - 注入编辑脚本
const onPreviewLoad = (isRescan = false) => {
  try {
    const iframe = previewFrame.value
    if (!iframe || !iframe.contentDocument) return
    
    const doc = iframe.contentDocument
    
    // 确保 body 已存在
    if (!doc.body) return
    
    // 先清理可能存在的旧样式（避免重复注入）
    const oldStyle = doc.getElementById('visual-editor-styles')
    if (oldStyle) oldStyle.remove()
    
    // 注入编辑样式（添加特殊标识，防止被保存）
    const style = doc.createElement('style')
    style.id = 'visual-editor-styles' // 添加ID用于清理
    style.setAttribute('data-editor-only', 'true') // 标记为编辑器专用
    style.textContent = `
      .visual-edit-hover {
        outline: 2px dashed #3B82F6 !important;
        outline-offset: 2px !important;
        cursor: pointer !important;
        position: relative !important;
      }
      .visual-edit-active {
        outline: 2px solid #10B981 !important;
        outline-offset: 2px !important;
        background: rgba(16, 185, 129, 0.05) !important;
      }
      .section-move-buttons {
        position: absolute !important;
        top: 10px !important;
        right: 10px !important;
        display: flex !important;
        gap: 4px !important;
        z-index: 10000 !important;
      }
      .section-move-btn {
        width: 32px !important;
        height: 32px !important;
        background: #3B82F6 !important;
        color: white !important;
        border: none !important;
        border-radius: 6px !important;
        cursor: pointer !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        font-size: 16px !important;
        font-weight: bold !important;
        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4) !important;
        transition: all 0.2s !important;
      }
      .section-move-btn:hover {
        background: #2563EB !important;
        transform: scale(1.1) !important;
      }
      .section-move-btn:active {
        transform: scale(0.95) !important;
      }
      .section-move-btn:disabled {
        background: #D1D5DB !important;
        cursor: not-allowed !important;
        opacity: 0.5 !important;
      }
      .section-move-btn.delete {
        background: #DC2626 !important;
      }
      .section-movable {
        outline: 2px dashed #3B82F6 !important;
        outline-offset: 2px !important;
        position: relative !important;
      }
      .hero-image-edit-button {
        position: absolute !important;
        bottom: 10px !important;
        right: 10px !important;
        width: 36px !important;
        height: 36px !important;
        background: #3B82F6 !important;
        color: white !important;
        border-radius: 6px !important;
        cursor: pointer !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        font-size: 18px !important;
        font-weight: bold !important;
        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.5) !important;
        transition: all 0.2s !important;
        z-index: 10001 !important;
        user-select: none !important;
      }
      .hero-image-edit-button:hover {
        background: #2563EB !important;
        transform: scale(1.1) !important;
      }
      .hero-image-edit-button:active {
        transform: scale(0.95) !important;
      }
      .video-edit-button {
        position: absolute !important;
        bottom: 10px !important;
        right: 10px !important;
        width: 36px !important;
        height: 36px !important;
        background: #3B82F6 !important;
        color: white !important;
        border-radius: 6px !important;
        cursor: pointer !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        font-size: 18px !important;
        font-weight: bold !important;
        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.5) !important;
        transition: all 0.2s !important;
        z-index: 10002 !important;
        user-select: none !important;
      }
      .video-edit-button:hover {
        background: #2563EB !important;
        transform: scale(1.1) !important;
      }
      .video-edit-button:active {
        transform: scale(0.95) !important;
      }
    `
    doc.head.appendChild(style)
    
    // 为所有可编辑元素添加hover效果和点击事件
    const elements = doc.querySelectorAll(editableSelectors)
    elements.forEach(element => {
      // 跳过编辑器控制元素（如Hero图片编辑按钮）
      if (element.hasAttribute('data-editor-control')) {
        return
      }
      
      // 跳过功能性按钮（关闭、取消、菜单等）
      if (element.tagName === 'BUTTON') {
        const id = element.getAttribute('id') || ''
        const className = element.className || ''
        const ariaLabel = element.getAttribute('aria-label') || ''
        
        // 跳过关闭按钮
        if (id.includes('close') || id.includes('modal') || id.includes('menu')) {
          return
        }
        
        // 跳过包含关闭相关class的按钮
        if (className.includes('close') || className.includes('dismiss') || className.includes('cancel')) {
          return
        }
        
        // 跳过只有 × 或 ✕ 符号的按钮
        const text = element.textContent.trim()
        if (text === '×' || text === '✕' || text === 'X' || text === '╳') {
          return
        }
        
        // 跳过 aria-label 包含关闭的按钮
        if (ariaLabel.toLowerCase().includes('close') || ariaLabel.toLowerCase().includes('dismiss')) {
          return
        }
      }
      
      // 跳过空元素和脚本（但保留图片和链接）
      if (element.tagName === 'SCRIPT' || element.tagName === 'STYLE') {
        return
      }
      // 对于非图片、非链接元素，跳过空内容
      // 链接即使只包含SVG图标也应该可以编辑
      if (element.tagName !== 'IMG' && element.tagName !== 'A' && !element.textContent.trim()) {
        return
      }
      
      // 对于div元素，只编辑带有文本样式类的div（如 text-xl, font-bold, text-gray-700）
      if (element.tagName === 'DIV') {
        const classList = element.className || ''
        const hasTextStyle = /text-xl|font-bold|font-semibold|font-medium|font-light/.test(classList)
        
        // 如果没有文本样式类，跳过（很可能是布局容器）
        if (!hasTextStyle) {
          return
        }
      }
      
      // 跳过已经添加过监听器的元素（避免重复注入）
      if (element.hasAttribute('data-visual-edit-enabled')) {
        return
      }
      element.setAttribute('data-visual-edit-enabled', 'true')
      
      // Hover效果
      element.addEventListener('mouseenter', (e) => {
        e.stopPropagation()
        element.classList.add('visual-edit-hover')
      })
      
      element.addEventListener('mouseleave', () => {
        element.classList.remove('visual-edit-hover')
      })
      
      // 点击编辑
      element.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        
        // 移除所有active状态
        doc.querySelectorAll('.visual-edit-active').forEach(el => {
          el.classList.remove('visual-edit-active')
        })
        
        element.classList.add('visual-edit-active')
        
        // 触发编辑
        editElement(element)
      })
    })
    const addBlockToolbar = (section) => {
      if (section.querySelector(':scope > .section-move-buttons')) return
      section.classList.add('section-movable')
      const buttonContainer = doc.createElement('div')
      buttonContainer.className = 'section-move-buttons'
      buttonContainer.setAttribute('data-editor-control', 'true')

      const upBtn = doc.createElement('button')
      upBtn.className = 'section-move-btn'
      upBtn.setAttribute('data-editor-control', 'true')
      upBtn.innerHTML = '↑'
      upBtn.title = '上移区块'
      upBtn.onclick = (e) => {
        e.preventDefault()
        e.stopPropagation()
        moveSectionUp(section)
      }

      const downBtn = doc.createElement('button')
      downBtn.className = 'section-move-btn'
      downBtn.setAttribute('data-editor-control', 'true')
      downBtn.innerHTML = '↓'
      downBtn.title = '下移区块'
      downBtn.onclick = (e) => {
        e.preventDefault()
        e.stopPropagation()
        moveSectionDown(section)
      }

      const deleteBtn = doc.createElement('button')
      deleteBtn.className = 'section-move-btn delete'
      deleteBtn.setAttribute('data-editor-control', 'true')
      deleteBtn.innerHTML = '删除'
      deleteBtn.title = '删除区块'
      deleteBtn.style.width = 'auto'
      deleteBtn.style.padding = '0 8px'
      deleteBtn.onclick = (e) => {
        e.preventDefault()
        e.stopPropagation()
        deleteSection(section)
      }

      const parent = section.parentElement
      const allSections = isVueBlocksPage()
        ? Array.from(parent.children).filter((item) => item.id)
        : Array.from(parent.querySelectorAll(movableSelectors))
      const currentIndex = allSections.indexOf(section)
      if (currentIndex === 0) upBtn.disabled = true
      if (currentIndex === allSections.length - 1) downBtn.disabled = true

      buttonContainer.appendChild(upBtn)
      buttonContainer.appendChild(downBtn)
      buttonContainer.appendChild(deleteBtn)
      section.appendChild(buttonContainer)
    }

    const blockRoots = doc.getElementById('app') && isVueBlocksPage()
      ? doc.querySelectorAll('#app > [id]')
      : doc.querySelectorAll(movableSelectors)
    blockRoots.forEach((section) => addBlockToolbar(section))

    if (isVueBlocksPage() && !doc.documentElement.hasAttribute('data-block-observer')) {
      const app = doc.getElementById('app')
      if (app) {
        doc.documentElement.setAttribute('data-block-observer', 'true')
        new MutationObserver(() => {
          app.querySelectorAll(':scope > [id]').forEach((section) => addBlockToolbar(section))
        }).observe(app, { childList: true })
      }
    }
    
    // 为Hero区域添加图片编辑按钮（处理被覆盖的背景图）
    const heroSections = doc.querySelectorAll('section')
    heroSections.forEach(section => {
      // 检测是否是Hero区域：包含背景图片 + absolute覆盖层
      const bgImage = section.querySelector('img')
      const absoluteOverlay = section.querySelector('div.absolute')
      
      if (bgImage && absoluteOverlay && !section.querySelector('.hero-image-edit-button')) {
        // 确保section是relative定位
        section.style.position = 'relative'
        
        // 创建图片编辑按钮（使用div避免被识别为可编辑元素）
        const editBtn = doc.createElement('div')
        editBtn.className = 'hero-image-edit-button'
        editBtn.innerHTML = '🖼️'
        editBtn.title = '编辑背景图片'
        editBtn.setAttribute('data-editor-control', 'true')  // 标记为编辑器控制元素
        
        // 点击编辑按钮
        editBtn.addEventListener('click', (e) => {
          e.preventDefault()
          e.stopPropagation()
          
          // 移除所有active状态
          doc.querySelectorAll('.visual-edit-active').forEach(el => {
            el.classList.remove('visual-edit-active')
          })
          
          bgImage.classList.add('visual-edit-active')
          
          // 触发编辑
          editElement(bgImage)
        })
        
        section.appendChild(editBtn)
      }
    })
    
    // 为视频播放器添加编辑按钮（检测所有YouTube iframe）
    const youtubeIframes = doc.querySelectorAll('iframe[src*="youtube.com/embed"], iframe[src*="youtu.be"]')
    youtubeIframes.forEach(videoIframe => {
      // 查找父容器（可能是 div 或 section）
      let videoContainer = videoIframe.parentElement
      
      // 如果找不到合适的容器，使用 iframe 的直接父元素
      if (!videoContainer) {
        videoContainer = videoIframe.parentNode
      }
      
      // 检查是否已添加编辑按钮
      if (videoContainer && !videoContainer.querySelector('.video-edit-button')) {
        // 确保容器是relative定位
        const computedStyle = window.getComputedStyle(videoContainer)
        if (computedStyle.position === 'static') {
          videoContainer.style.position = 'relative'
        }
        
        // 创建视频编辑按钮
        const videoEditBtn = doc.createElement('div')
        videoEditBtn.className = 'video-edit-button'
        videoEditBtn.innerHTML = '🎬'
        videoEditBtn.title = '编辑视频ID'
        videoEditBtn.setAttribute('data-editor-control', 'true')
        
        // 点击编辑按钮
        videoEditBtn.addEventListener('click', (e) => {
          e.preventDefault()
          e.stopPropagation()
          
          // 触发视频编辑
          editVideo(videoIframe)
        })
        
        videoContainer.appendChild(videoEditBtn)
      }
    })
    if (!isRescan) {
      setTimeout(() => onPreviewLoad(true), 1000)
      setTimeout(() => onPreviewLoad(true), 2500)
    }
  } catch (error) {
    // 静默处理注入错误
  }
}

// 上移section
const moveSectionUp = (section) => {
  const parent = section.parentElement
  const previousSibling = section.previousElementSibling
  
  // 找到前一个section元素
  let prevSection = previousSibling
  while (prevSection && prevSection.tagName !== 'SECTION') {
    prevSection = prevSection.previousElementSibling
  }
  
  if (isVueBlocksPage()) {
    if (moveVueBlock(section, -1)) toast.success('区块已上移')
    return
  }

  if (prevSection) {
    parent.insertBefore(section, prevSection)
    
    // 更新HTML代码（直接同步，保留所有编辑器标记）
    const iframe = previewFrame.value
    if (iframe && iframe.contentDocument) {
      // 临时移除移动按钮，避免保存到HTML中
      iframe.contentDocument.querySelectorAll('.section-move-buttons').forEach(btn => btn.remove())
      
      // 标记跳过预览刷新，因为DOM已经是最新的
      skipNextPreviewUpdate.value = true
      
      // 同步HTML（保留编辑器标记）
      htmlContent.value = iframe.contentDocument.documentElement.outerHTML
      
      // 重新注入编辑脚本（不刷新iframe）
      nextTick(() => {
        onPreviewLoad()
      })
    }
    
    toast.success('区块已上移')
  }
}

// 下移section
const moveSectionDown = (section) => {
  const parent = section.parentElement
  const nextSibling = section.nextElementSibling
  
  // 找到后一个section元素
  let nextSection = nextSibling
  while (nextSection && nextSection.tagName !== 'SECTION') {
    nextSection = nextSection.nextElementSibling
  }
  
  if (isVueBlocksPage()) {
    if (moveVueBlock(section, 1)) toast.success('区块已下移')
    return
  }

  if (nextSection) {
    parent.insertBefore(section, nextSection.nextElementSibling)
    
    // 更新HTML代码（直接同步，保留所有编辑器标记）
    const iframe = previewFrame.value
    if (iframe && iframe.contentDocument) {
      // 临时移除移动按钮，避免保存到HTML中
      iframe.contentDocument.querySelectorAll('.section-move-buttons').forEach(btn => btn.remove())
      
      // 标记跳过预览刷新，因为DOM已经是最新的
      skipNextPreviewUpdate.value = true
      
      // 同步HTML（保留编辑器标记）
      htmlContent.value = iframe.contentDocument.documentElement.outerHTML
      
      // 重新注入编辑脚本（不刷新iframe）
      nextTick(() => {
        onPreviewLoad()
      })
    }
    
    toast.success('区块已下移')
  }
}

const deleteSection = (section) => {
  if (!window.confirm('删除这个板块？')) return
  if (isVueBlocksPage()) {
    if (!deleteVueBlock(section)) {
      toast.warning('这个板块删不掉')
      return
    }
    toast.success('板块已删除')
    return
  }
  section.remove()
  const iframe = previewFrame.value
  if (iframe && iframe.contentDocument) {
    iframe.contentDocument.querySelectorAll('.section-move-buttons').forEach(btn => btn.remove())
    skipNextPreviewUpdate.value = true
    htmlContent.value = iframe.contentDocument.documentElement.outerHTML
    nextTick(() => {
      onPreviewLoad()
    })
  }
  toast.success('板块已删除')
}

// 编辑元素状态
const editingElement = ref(null)
const editingContent = ref('')
const editingTagName = ref('')
const showEditModal = ref(false)
const contentEditor = ref(null)
const imageFileInput = ref(null)

// 富文本编辑状态
const textColor = ref('#000000')
const bgColor = ref('#FFFFFF')
const fontSize = ref('')

// 链接编辑状态
const showLinkDialog = ref(false)
const linkUrl = ref('')
const linkText = ref('')
const linkTargetBlank = ref(true)
const linkColor = ref('#3B82F6') // 默认蓝色
const originalLinkColor = ref('#3B82F6') // 原始颜色（用于判断是否修改）
const isEditingLink = ref(false)
const currentLinkElement = ref(null)
const linkContainsOnlySvg = ref(false) // 链接是否只包含SVG图标

// 当前编辑的链接信息（从iframe中获取）
const originalLinkInfo = ref(null)

// 图片编辑状态
const isEditingImage = ref(false)
const imageSrc = ref('')
const imageAlt = ref('')

// 视频编辑状态
const showVideoDialog = ref(false)
const videoId = ref('')
const editingVideoElement = ref(null)

// RGB颜色转十六进制的工具函数
const rgbToHex = (rgb) => {
  if (!rgb) return null
  
  // 如果已经是十六进制格式
  if (rgb.startsWith('#')) {
    return rgb
  }
  
  // 解析 rgb(r, g, b) 或 rgba(r, g, b, a) 格式
  const match = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/)
  if (!match) return null
  
  const r = parseInt(match[1])
  const g = parseInt(match[2])
  const b = parseInt(match[3])
  
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }).join('')
}

// 提取YouTube视频ID的工具函数
const extractYouTubeId = (url) => {
  if (!url) return ''
  
  // 如果已经是纯ID，直接返回
  if (url.length === 11 && !url.includes('/') && !url.includes('?')) {
    return url
  }
  
  // 匹配各种YouTube URL格式
  const patterns = [
    /(?:youtube\.com\/embed\/|youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
    /[?&]v=([a-zA-Z0-9_-]{11})/
  ]
  
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match && match[1]) {
      return match[1]
    }
  }
  
  return url
}

// 编辑视频
const editVideo = (videoIframe) => {
  editingVideoElement.value = videoIframe
  
  // 从iframe的src中提取视频ID
  const currentSrc = videoIframe.getAttribute('src') || ''
  const extractedId = extractYouTubeId(currentSrc)
  videoId.value = extractedId
  
  showVideoDialog.value = true
}

// 保存视频ID
const saveVideoId = () => {
  if (!videoId.value) {
    toast.error('请输入YouTube视频ID')
    return
  }
  
  if (!editingVideoElement.value) return
  
  // 提取纯ID
  const cleanId = extractYouTubeId(videoId.value)
  
  if (cleanId.length !== 11) {
    toast.error('视频ID格式不正确，应为11位字符')
    return
  }
  
  // 获取当前URL，保留参数
  const currentSrc = editingVideoElement.value.getAttribute('src') || ''
  let newSrc = ''
  
  if (currentSrc.includes('?')) {
    // 保留现有参数，只替换视频ID
    const baseUrl = 'https://www.youtube.com/embed/' + cleanId
    const params = currentSrc.split('?')[1] || ''
    // 更新playlist参数（如果存在）
    const updatedParams = params.replace(/playlist=[^&]+/, `playlist=${cleanId}`)
    newSrc = baseUrl + '?' + updatedParams
  } else {
    // 没有参数，使用默认参数
    newSrc = `https://www.youtube.com/embed/${cleanId}?autoplay=0&controls=1&modestbranding=1&rel=0&fs=1&playsinline=1&loop=1&playlist=${cleanId}`
  }
  
  // 更新iframe的src
  editingVideoElement.value.setAttribute('src', newSrc)
  
  // 同步更新HTML代码
  const iframe = previewFrame.value
  if (iframe && iframe.contentDocument) {
    // 临时移除编辑按钮
    iframe.contentDocument.querySelectorAll('.video-edit-button').forEach(btn => btn.remove())
    
    skipNextPreviewUpdate.value = true
    htmlContent.value = iframe.contentDocument.documentElement.outerHTML
    
    // 重新注入编辑脚本
    nextTick(() => {
      onPreviewLoad()
    })
  }
  
  closeVideoDialog()
  toast.success('视频ID已更新')
}

// 关闭视频编辑对话框
const closeVideoDialog = () => {
  showVideoDialog.value = false
  videoId.value = ''
  editingVideoElement.value = null
}

// 处理图片上传
const handleImageUpload = (event) => {
  const file = event.target.files?.[0]
  if (!file) return
  
  // 检查文件类型
  if (!file.type.startsWith('image/')) {
    toast.error('请选择图片文件')
    return
  }
  
  // 检查文件大小（限制为 5MB）
  const maxSize = 5 * 1024 * 1024 // 5MB
  if (file.size > maxSize) {
    toast.error('图片大小不能超过 5MB')
    return
  }
  
  // 读取文件并转换为 base64
  const reader = new FileReader()
  reader.onload = (e) => {
    const base64 = e.target.result
    imageSrc.value = base64
    toast.success('图片已上传')
  }
  reader.onerror = () => {
    toast.error('图片读取失败')
  }
  reader.readAsDataURL(file)
  
  // 清空 input，允许重复选择同一文件
  event.target.value = ''
}

// 编辑元素
const editElement = (element) => {
  // 移除hover样式
  element.classList.remove('visual-edit-hover')
  
  editingElement.value = element
  editingContent.value = element.innerHTML
  editingTagName.value = element.tagName.toLowerCase()
  
  // 如果点击的是图片，特殊处理
  if (element.tagName === 'IMG') {
    isEditingImage.value = true
    imageSrc.value = element.getAttribute('src') || ''
    imageAlt.value = element.getAttribute('alt') || ''
    originalLinkInfo.value = null
    showEditModal.value = true
    
    // 等待DOM更新后设置contenteditable内容
    nextTick(() => {
      if (contentEditor.value) {
        contentEditor.value.innerHTML = element.innerHTML
        contentEditor.value.focus()
      }
    })
  }
  // 如果点击的元素本身就是链接，直接打开链接编辑对话框
  else if (element.tagName === 'A') {
    isEditingImage.value = false
    
    // 检查链接是否只包含SVG图标（没有实际文本）
    const hasSvg = element.querySelector('svg') !== null
    const textContent = element.textContent.trim()
    linkContainsOnlySvg.value = hasSvg && textContent === ''
    
    originalLinkInfo.value = {
      href: element.getAttribute('href') || '',
      text: element.textContent || '',
      target: element.getAttribute('target') || '',
      rel: element.getAttribute('rel') || ''
    }
    
    // 直接打开链接编辑对话框
    isEditingLink.value = true
    currentLinkElement.value = null
    linkUrl.value = originalLinkInfo.value.href
    linkText.value = originalLinkInfo.value.text
    linkTargetBlank.value = originalLinkInfo.value.target === '_blank'
    
    // 获取链接颜色（从iframe中的元素获取）
    const iframe = previewFrame.value
    if (iframe && iframe.contentWindow) {
      const iframeWindow = iframe.contentWindow
      const computedStyle = iframeWindow.getComputedStyle(element)
      const extractedColor = rgbToHex(computedStyle.color) || '#3B82F6'
      linkColor.value = extractedColor
      originalLinkColor.value = extractedColor // 保存原始颜色
    } else {
      linkColor.value = '#3B82F6'
      originalLinkColor.value = '#3B82F6'
    }
    
    showLinkDialog.value = true
  } else {
    isEditingImage.value = false
    originalLinkInfo.value = null
    showEditModal.value = true
    
    // 等待DOM更新后设置contenteditable内容
    nextTick(() => {
      if (contentEditor.value) {
        contentEditor.value.innerHTML = element.innerHTML
        contentEditor.value.focus()
      }
    })
  }
}

// 内容输入事件
const onContentInput = () => {
  if (contentEditor.value) {
    editingContent.value = contentEditor.value.innerHTML
  }
}

// 内容失焦事件
const onContentBlur = () => {
  if (contentEditor.value) {
    editingContent.value = contentEditor.value.innerHTML
  }
}

// 应用文字颜色
const applyTextColor = () => {
  document.execCommand('foreColor', false, textColor.value)
  contentEditor.value?.focus()
}

// 应用背景颜色
const applyBgColor = () => {
  document.execCommand('backColor', false, bgColor.value)
  contentEditor.value?.focus()
}

// 应用格式（加粗、斜体、下划线）
const applyFormat = (format) => {
  document.execCommand(format, false, null)
  contentEditor.value?.focus()
}

// 应用字体大小
const applyFontSize = () => {
  if (fontSize.value) {
    document.execCommand('fontSize', false, '7') // 先设置一个临时值
    // 然后用CSS替换
    const fontElements = contentEditor.value?.querySelectorAll('font[size="7"]')
    fontElements?.forEach(el => {
      el.removeAttribute('size')
      el.style.fontSize = fontSize.value
    })
  }
  contentEditor.value?.focus()
}

// 清除格式
const clearFormat = () => {
  const selection = window.getSelection()
  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0)
    const selectedText = range.toString()
    
    if (selectedText) {
      // 替换选中的内容为纯文本
      range.deleteContents()
      range.insertNode(document.createTextNode(selectedText))
    } else {
      // 如果没有选中，清除整个内容的格式
      if (contentEditor.value) {
        const text = contentEditor.value.textContent
        contentEditor.value.innerHTML = text
      }
    }
  }
  contentEditor.value?.focus()
}

// 打开链接对话框时检测是否在编辑已有链接
const openLinkDialog = () => {
  // 优先检查：如果当前编辑的元素本身就是链接（从iframe中点击的）
  if (originalLinkInfo.value) {
    isEditingLink.value = true
    currentLinkElement.value = null // contentEditor中还没有对应的元素
    linkUrl.value = originalLinkInfo.value.href
    linkText.value = originalLinkInfo.value.text
    linkTargetBlank.value = originalLinkInfo.value.target === '_blank'
    
    // 获取链接颜色
    if (editingElement.value) {
      const computedStyle = window.getComputedStyle(editingElement.value)
      const extractedColor = rgbToHex(computedStyle.color) || '#3B82F6'
      linkColor.value = extractedColor
      originalLinkColor.value = extractedColor // 保存原始颜色
    }
    
    showLinkDialog.value = true
    return
  }
  
  // 检查 contentEditor 中的所有链接
  if (contentEditor.value) {
    const links = contentEditor.value.querySelectorAll('a')
    
    // 如果只有一个链接，直接编辑它
    if (links.length === 1) {
      const link = links[0]
      isEditingLink.value = true
      currentLinkElement.value = link
      linkUrl.value = link.getAttribute('href') || ''
      linkText.value = link.textContent || ''
      linkTargetBlank.value = link.getAttribute('target') === '_blank'
      
      // 获取链接颜色
      const computedStyle = window.getComputedStyle(link)
      const extractedColor = rgbToHex(computedStyle.color) || '#3B82F6'
      linkColor.value = extractedColor
      originalLinkColor.value = extractedColor // 保存原始颜色
      
      showLinkDialog.value = true
      return
    }
    
    // 如果有多个链接，检查光标位置
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      
      // 检查选中的元素或其父元素是否是链接
      let element = range.commonAncestorContainer
      if (element.nodeType === 3) { // 文本节点
        element = element.parentElement
      }
      
      // 查找最近的 <a> 标签
      while (element && element !== contentEditor.value) {
        if (element.tagName === 'A') {
          // 正在编辑已有链接
          isEditingLink.value = true
          currentLinkElement.value = element
          linkUrl.value = element.getAttribute('href') || ''
          linkText.value = element.textContent || ''
          linkTargetBlank.value = element.getAttribute('target') === '_blank'
          
          // 获取链接颜色
          const computedStyle = window.getComputedStyle(element)
          const extractedColor = rgbToHex(computedStyle.color) || '#3B82F6'
          linkColor.value = extractedColor
          originalLinkColor.value = extractedColor // 保存原始颜色
          
          showLinkDialog.value = true
          return
        }
        element = element.parentElement
      }
      
      // 新建链接
      isEditingLink.value = false
      currentLinkElement.value = null
      linkUrl.value = ''
      linkText.value = selection.toString() || ''
      linkTargetBlank.value = true
      linkColor.value = '#3B82F6'
      originalLinkColor.value = '#3B82F6' // 新建链接，保存默认颜色
    } else {
      // 没有选中内容，创建新链接
      isEditingLink.value = false
      currentLinkElement.value = null
      linkUrl.value = ''
      linkText.value = ''
      linkTargetBlank.value = true
      linkColor.value = '#3B82F6'
      originalLinkColor.value = '#3B82F6' // 新建链接，保存默认颜色
    }
  }
  
  showLinkDialog.value = true
}

// 应用链接
const applyLink = () => {
  if (!linkUrl.value) {
    toast.error('请输入链接地址')
    return
  }
  
  // 确保URL格式正确
  let url = linkUrl.value
  if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('/') && !url.startsWith('#')) {
    url = 'https://' + url
  }
  
  if (isEditingLink.value && originalLinkInfo.value) {
    // 编辑从iframe点击的链接（更新editingElement）
    if (editingElement.value && editingElement.value.tagName === 'A') {
      const previousHref = editingElement.value.getAttribute('href') || ''
      const previousText = editingElement.value.textContent || ''
      editingElement.value.setAttribute('href', url)
      editingElement.value.textContent = linkText.value || url
      
      // 只有当颜色被修改时才应用新颜色
      if (linkColor.value !== originalLinkColor.value) {
        editingElement.value.style.color = linkColor.value
      }
      
      if (linkTargetBlank.value) {
        editingElement.value.setAttribute('target', '_blank')
        editingElement.value.setAttribute('rel', 'noopener noreferrer')
      } else {
        editingElement.value.removeAttribute('target')
        editingElement.value.removeAttribute('rel')
      }
      
      // 同步更新HTML代码
      const iframe = previewFrame.value
      if (iframe && iframe.contentDocument) {
        skipNextPreviewUpdate.value = true
        if (isVueBlocksPage()) {
          const saved = saveVueBlockEdit(editingElement.value, previousText, linkText.value || url, previousHref, url)
          if (!saved) toast.warning('这段文字没有写进对应板块，请再点一次板块里的文字后保存')
        } else {
          htmlContent.value = iframe.contentDocument.documentElement.outerHTML
        }
      }
    }
  } else if (isEditingLink.value && currentLinkElement.value) {
    // 更新contentEditor中的已有链接
    currentLinkElement.value.setAttribute('href', url)
    currentLinkElement.value.textContent = linkText.value || url
    
    // 只有当颜色被修改时才应用新颜色
    if (linkColor.value !== originalLinkColor.value) {
      currentLinkElement.value.style.color = linkColor.value
    }
    
    if (linkTargetBlank.value) {
      currentLinkElement.value.setAttribute('target', '_blank')
      currentLinkElement.value.setAttribute('rel', 'noopener noreferrer')
    } else {
      currentLinkElement.value.removeAttribute('target')
      currentLinkElement.value.removeAttribute('rel')
    }
  } else {
    // 创建新链接
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      
      // 创建链接元素
      const link = document.createElement('a')
      link.setAttribute('href', url)
      
      // 新建链接时，只有当颜色不是默认颜色时才应用
      if (linkColor.value !== '#3B82F6') {
        link.style.color = linkColor.value
      }
      
      if (linkTargetBlank.value) {
        link.setAttribute('target', '_blank')
        link.setAttribute('rel', 'noopener noreferrer')
      }
      link.textContent = linkText.value || url
      
      // 删除选中的内容并插入链接
      range.deleteContents()
      range.insertNode(link)
      
      // 将光标移到链接后面
      range.setStartAfter(link)
      range.setEndAfter(link)
      selection.removeAllRanges()
      selection.addRange(range)
    }
  }
  
  closeLinkDialog()
  contentEditor.value?.focus()
  
  // 更新内容
  onContentInput()
  
  toast.success(isEditingLink.value ? '链接已更新' : '链接已添加')
}

// 移除链接
const removeLink = () => {
  if (originalLinkInfo.value && editingElement.value && editingElement.value.tagName === 'A') {
    // 移除从iframe点击的链接
    const text = editingElement.value.textContent
    const textNode = document.createTextNode(text)
    editingElement.value.parentNode?.replaceChild(textNode, editingElement.value)
    
    // 更新contentEditor
    if (contentEditor.value) {
      contentEditor.value.innerHTML = text
    }
    editingContent.value = text
    
    closeLinkDialog()
    toast.success('链接已移除')
  } else if (currentLinkElement.value) {
    // 移除contentEditor中的链接
    const text = currentLinkElement.value.textContent
    const textNode = document.createTextNode(text)
    currentLinkElement.value.parentNode?.replaceChild(textNode, currentLinkElement.value)
    
    closeLinkDialog()
    contentEditor.value?.focus()
    onContentInput()
    
    toast.success('链接已移除')
  }
}

// 关闭链接对话框
const closeLinkDialog = () => {
  showLinkDialog.value = false
  linkUrl.value = ''
  linkText.value = ''
  linkTargetBlank.value = true
  linkColor.value = '#3B82F6'
  originalLinkColor.value = '#3B82F6'
  isEditingLink.value = false
  currentLinkElement.value = null
  linkContainsOnlySvg.value = false
  
  // 清除所有编辑状态的 active 边框
  const iframe = previewFrame.value
  if (iframe && iframe.contentDocument) {
    iframe.contentDocument.querySelectorAll('.visual-edit-active').forEach(el => {
      el.classList.remove('visual-edit-active')
    })
  }
}

const patchTemplateValue = (oldValue, newValue) => {
  if (oldValue == null || oldValue === newValue) return true
  const source = htmlContent.value || ''
  const oldJson = JSON.stringify(String(oldValue)).slice(1, -1)
  const newJson = JSON.stringify(String(newValue)).slice(1, -1)
  if (oldJson && source.includes(oldJson)) {
    htmlContent.value = source.replace(oldJson, newJson)
    return true
  }
  if (source.includes(oldValue)) {
    htmlContent.value = source.replace(oldValue, newValue)
    return true
  }
  return false
}

const isVueBlocksPage = () => {
  const html = htmlContent.value || ''
  return html.includes('Vue.createApp') && html.includes('blocks:')
}

const locateBlocksArray = (html) => {
  const keyAt = html.indexOf('blocks:')
  if (keyAt < 0) return null
  const start = html.indexOf('[', keyAt)
  if (start < 0 || start - keyAt > 30) return null
  let depth = 0
  let inString = false
  let escaped = false
  for (let i = start; i < html.length; i++) {
    const ch = html[i]
    if (inString) {
      if (escaped) escaped = false
      else if (ch === '\\') escaped = true
      else if (ch === '"') inString = false
      continue
    }
    if (ch === '"') inString = true
    else if (ch === '[' || ch === '{') depth++
    else if (ch === ']' || ch === '}') {
      depth--
      if (depth === 0 && ch === ']') return { start, end: i + 1 }
    }
  }
  return null
}

const updateVueBlocks = (mutator) => {
  const html = htmlContent.value || ''
  const loc = locateBlocksArray(html)
  if (!loc) return false
  let blocks
  try {
    blocks = JSON.parse(html.slice(loc.start, loc.end))
  } catch {
    return false
  }
  const next = mutator(blocks)
  if (!next) return false
  htmlContent.value = html.slice(0, loc.start) + JSON.stringify(next) + html.slice(loc.end)
  return true
}

const appBlockElement = (element) => {
  const app = element.ownerDocument && element.ownerDocument.getElementById('app')
  if (!app) return null
  let node = element
  while (node && node.parentElement !== app) node = node.parentElement
  return node && node.id ? node : null
}

const replaceVueValue = (value, from, to) => {
  const needle = (from || '').trim()
  if (!needle || needle === to) return { value, changed: false }
  if (typeof value === 'string') {
    if (value === from || value.trim() === needle) return { value: to, changed: true }
    return { value, changed: false }
  }
  if (Array.isArray(value)) {
    let changed = false
    const next = value.map((item) => {
      const result = replaceVueValue(item, from, to)
      if (result.changed) changed = true
      return result.value
    })
    return { value: changed ? next : value, changed }
  }
  if (value && typeof value === 'object') {
    let changed = false
    const next = { ...value }
    Object.keys(next).forEach((key) => {
      const result = replaceVueValue(next[key], from, to)
      if (result.changed) {
        next[key] = result.value
        changed = true
      }
    })
    return { value: next, changed }
  }
  return { value, changed: false }
}

const saveVueBlockEdit = (element, oldText, newText, oldSrc, newSrc) => {
  const root = appBlockElement(element)
  const blockId = root && root.id
  if (!blockId) return false
  return updateVueBlocks((blocks) => {
    const block = blocks.find((item) => item.params && item.params.id === blockId)
    if (!block) return null
    let changed = false
    if ((oldText || '').trim() && (oldText || '').trim() !== (newText || '').trim()) {
      const result = replaceVueValue(block.params, oldText, newText || '')
      if (result.changed) {
        block.params = result.value
        changed = true
      }
    }
    if (oldSrc && newSrc && oldSrc !== newSrc) {
      const result = replaceVueValue(block.params, oldSrc, newSrc)
      if (result.changed) {
        block.params = result.value
        changed = true
      }
    }
    if (!changed && (oldText || '') === (newText || '') && (!newSrc || oldSrc === newSrc)) return blocks
    return changed ? blocks : null
  })
}

const moveVueBlock = (section, direction) => {
  const app = section.ownerDocument.getElementById('app')
  let node = section
  while (node && node.parentElement !== app) node = node.parentElement
  if (!app || !node || !node.id) return false
  const siblings = Array.from(app.children).filter((item) => item.id)
  const index = siblings.indexOf(node)
  const target = index + direction
  if (index < 0 || target < 0 || target >= siblings.length) return false
  if (direction < 0) app.insertBefore(node, siblings[target])
  else app.insertBefore(siblings[target], node)
  const movingId = node.id
  const otherId = siblings[target].id
  return updateVueBlocks((blocks) => {
    const from = blocks.findIndex((item) => item.params && item.params.id === movingId)
    const to = blocks.findIndex((item) => item.params && item.params.id === otherId)
    if (from < 0 || to < 0) return null
    const swapped = blocks[from]
    blocks[from] = blocks[to]
    blocks[to] = swapped
    return blocks
  })
}

const deleteVueBlock = (section) => {
  const app = section.ownerDocument.getElementById('app')
  let node = section
  while (node && node.parentElement !== app) node = node.parentElement
  if (!node || !node.id) return false
  const blockId = node.id
  node.remove()
  return updateVueBlocks((blocks) => blocks.filter((item) => !(item.params && item.params.id === blockId)))
}

const usesGeneratedBlocks = () => isVueBlocksPage()

// 保存编辑
const saveEdit = () => {
  if (!editingElement.value) return
  const previousText = editingElement.value.textContent
  const previousSrc = editingElement.value.getAttribute('src')
  
  // 如果是图片编辑
  if (isEditingImage.value) {
    // 更新图片的src和alt属性
    editingElement.value.setAttribute('src', imageSrc.value)
    editingElement.value.setAttribute('alt', imageAlt.value)
  } 
  // 普通元素编辑
  else {
    if (!contentEditor.value) return
    // 从contenteditable获取最新内容
    const newContent = contentEditor.value.innerHTML
    // 更新预览中的元素
    editingElement.value.innerHTML = newContent
  }
  
  // 同步更新HTML代码（保留编辑器标记）
  const iframe = previewFrame.value
  if (iframe && iframe.contentDocument) {
    // 临时移除移动按钮和活动状态，避免保存到HTML中
    iframe.contentDocument.querySelectorAll('.section-move-buttons').forEach(btn => btn.remove())
    iframe.contentDocument.querySelectorAll('.visual-edit-active, .visual-edit-hover, .section-movable').forEach(el => {
      el.classList.remove('visual-edit-active', 'visual-edit-hover', 'section-movable')
    })
    
    // 标记跳过预览刷新，因为DOM已经是最新的
    skipNextPreviewUpdate.value = true

    if (isVueBlocksPage()) {
      const saved = saveVueBlockEdit(
        editingElement.value,
        previousText,
        editingElement.value.textContent,
        previousSrc,
        editingElement.value.getAttribute('src') || ''
      )
      if (!saved) toast.warning('这段文字没有写进对应板块，请再点一次板块里的文字后保存')
    } else {
      htmlContent.value = iframe.contentDocument.documentElement.outerHTML
    }
    
    // 重新注入编辑脚本（不刷新iframe）
    nextTick(() => {
      onPreviewLoad()
    })
  }
  
  closeEditModal()
  toast.success('内容已更新')
}

// 关闭编辑模态框
const closeEditModal = () => {
  showEditModal.value = false
  editingElement.value = null
  editingContent.value = ''
  editingTagName.value = ''
  
  // 重置富文本编辑状态
  textColor.value = '#000000'
  bgColor.value = '#FFFFFF'
  fontSize.value = ''
  
  // 重置链接信息
  originalLinkInfo.value = null
  closeLinkDialog()
  
  // 重置图片编辑状态
  isEditingImage.value = false
  imageSrc.value = ''
  imageAlt.value = ''
  if (imageFileInput.value) {
    imageFileInput.value.value = ''
  }
  
  // 清空编辑器内容
  if (contentEditor.value) {
    contentEditor.value.innerHTML = ''
  }
  
  // 移除临时高亮状态
  const iframe = previewFrame.value
  if (iframe && iframe.contentDocument) {
    iframe.contentDocument.querySelectorAll('.visual-edit-active, .visual-edit-hover, .section-movable').forEach(el => {
      el.classList.remove('visual-edit-active', 'visual-edit-hover', 'section-movable')
    })
    
    // 清理临时移动按钮（鼠标移出时会自动删除，这里是清理残留）
    iframe.contentDocument.querySelectorAll('.section-move-buttons').forEach(btn => {
      btn.remove()
    })
  }
}

// 格式化代码（简单版本）
const formatCode = () => {
  try {
    // 简单的HTML格式化
    let formatted = htmlContent.value
    
    // 移除多余空白
    formatted = formatted.trim()
    
    toast.success('代码已格式化')
    // 注意：真正的格式化需要HTML解析器，这里只是示意
  } catch (error) {
    toast.error('格式化失败')
  }
}

// 批量替换文字
const replaceAllText = () => {
  if (!searchText.value) {
    toast.error('请输入要查找的文字')
    return
  }
  
  const regex = new RegExp(searchText.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')
  const matches = htmlContent.value.match(regex)
  
  if (!matches || matches.length === 0) {
    toast.warning('未找到匹配的文字')
    return
  }
  
  htmlContent.value = htmlContent.value.replace(regex, replaceText.value)
  
  toast.success(`已替换 ${matches.length} 处文字`)
  showTextReplacer.value = false
  searchText.value = ''
  replaceText.value = ''
  updatePreview()
}

// 清理HTML中的所有编辑器注入内容（供外部保存时调用）
const getCleanHTML = () => {
  let html = htmlContent.value
  if (!html) return html
  
  // 移除编辑器注入的样式标签
  html = html.replace(/<style[^>]*id=["']visual-editor-styles["'][^>]*>[\s\S]*?<\/style>/gi, '')
  html = html.replace(/<style[^>]*data-editor-only=["']true["'][^>]*>[\s\S]*?<\/style>/gi, '')
  
  // 🎯 移除 Tailwind CDN 动态生成的样式
  // Tailwind 生成的 style 标签有唯一特征：以 "*, ::before, ::after{" 开头
  // 用户自定义样式（如 .wave-bg）不会以这个开头，所以不会被误删
  html = html.replace(/<style[^>]*>[\s\n\r\t]*\*[\s\n\r\t]*,[\s\n\r\t]*::before[\s\n\r\t]*,[\s\n\r\t]*::after[\s\n\r\t]*\{[\s\S]*?<\/style>/gi, '')
  
  // 移除编辑器注入的标记属性
  html = html.replace(/\s*data-visual-edit-enabled=["']true["']/gi, '')
  html = html.replace(/\s*data-section-move-enabled=["']true["']/gi, '')
  html = html.replace(/\s*data-editor-control=["']true["']/gi, '')
  
  // 移除Hero图片编辑按钮
  html = html.replace(/<div[^>]*class=["'][^"']*hero-image-edit-button[^"']*["'][^>]*>.*?<\/div>/gi, '')
  
  // 移除视频编辑按钮
  html = html.replace(/<div[^>]*class=["'][^"']*video-edit-button[^"']*["'][^>]*>.*?<\/div>/gi, '')
  
  // 移除section移动按钮
  html = html.replace(/<div[^>]*class=["'][^"']*section-move-buttons[^"']*["'][^>]*>[\s\S]*?<\/div>/gi, '')
  
  // 移除临时样式类
  html = html.replace(/\s*class=["']([^"']*)visual-edit-(hover|active)([^"']*)["']/gi, (match, before, type, after) => {
    const cleaned = (before + after).trim()
    return cleaned ? ` class="${cleaned}"` : ''
  })
  html = html.replace(/\s*class=["']([^"']*)section-movable([^"']*)["']/gi, (match, before, after) => {
    const cleaned = (before + after).trim()
    return cleaned ? ` class="${cleaned}"` : ''
  })
  
  return html
}

// 初始化预览
updatePreview()

// 暴露方法
defineExpose({
  refreshPreview,
  formatCode,
  getHTML: () => htmlContent.value,
  getCleanHTML, // 供外部保存时使用
  setHTML: (html) => {
    htmlContent.value = html
    updatePreview()
  }
})
</script>

<style scoped>
.html-editor {
  position: relative;
}

/* 自定义滚动条 */
textarea::-webkit-scrollbar,
.overflow-auto::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

textarea::-webkit-scrollbar-track,
.overflow-auto::-webkit-scrollbar-track {
  background: #1f2937;
}

textarea::-webkit-scrollbar-thumb,
.overflow-auto::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 4px;
}

textarea::-webkit-scrollbar-thumb:hover,
.overflow-auto::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

/* 行号效果（可选） */
textarea {
  line-height: 1.5;
  tab-size: 2;
}

/* 预览iframe样式 */
iframe {
  min-height: 400px;
}
</style>

